import logo from './logo.svg';
import './App.css';
import { ArrowRight, Users, TrendingUp, Shield, Star, ChevronDown, CheckCircle, ArrowUpRight, Menu, X } from 'lucide-react';
import { Parallax } from 'react-parallax';
import { useState } from 'react';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false); // Close mobile menu after clicking
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Header */}
      <header className="relative bg-slate-950/98 backdrop-blur-sm py-6 px-6 shadow-2xl border-b border-yellow-400/10 sticky top-0 z-[60]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="absolute inset-0 bg-yellow-400/20 blur-xl rounded-full"></div>
              <img 
                src="/OFA.png" 
                alt="OneForAll Logo" 
                className="relative h-16 w-auto object-contain"
              />
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-black text-yellow-400">ONE<span className="text-yellow-300">FOR</span>ALL</h1>
              <p className="text-xs text-gray-400 tracking-wide">INVESTMENT SYNDICATE</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('mission')}
              className="text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 relative group"
            >
              Our Mission
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('success-story')}
              className="text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 relative group"
            >
              Success Story
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('community')}
              className="text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 relative group"
            >
              Community
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 relative group"
            >
              How It Works
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></div>
            </button>
          </nav>

          {/* CTA Button and Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <a 
              href="https:/calendly.com/loic-cluboneforall/30min"
              className="group relative bg-gradient-to-r from-yellow-400 to-yellow-300 text-slate-950 px-6 py-2.5 lg:px-8 lg:py-3 rounded-full font-bold hover:from-yellow-300 hover:to-yellow-200 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-400/25 text-sm lg:text-base inline-block"
            >
              <span className="relative z-10">Contact me</span>
              <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-yellow-400 hover:text-yellow-300 transition-colors duration-300"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-slate-950 border-b border-yellow-400/10 transition-all duration-300 shadow-2xl ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <div className="max-w-7xl mx-auto px-6 py-6">
            <nav className="flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('mission')}
                className="text-left text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 py-2 border-b border-gray-800 hover:border-yellow-400/30"
              >
                Our Mission
              </button>
              <button 
                onClick={() => scrollToSection('success-story')}
                className="text-left text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 py-2 border-b border-gray-800 hover:border-yellow-400/30"
              >
                Success Story
              </button>
              <button 
                onClick={() => scrollToSection('community')}
                className="text-left text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 py-2 border-b border-gray-800 hover:border-yellow-400/30"
              >
                Community
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-left text-gray-300 hover:text-yellow-400 font-medium transition-colors duration-300 py-2"
              >
                How It Works
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Very Dark Blue */}
      <Parallax
        bgImage="data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23facc15' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"
        strength={300}
        bgImageStyle={{opacity: 0.3}}
      >
        <section className="relative py-24 px-6 overflow-hidden bg-slate-950">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/3 via-transparent to-transparent"></div>
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-yellow-400/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-800/10 rounded-full blur-3xl"></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400/10 to-yellow-300/10 backdrop-blur-sm rounded-full px-6 py-2 border border-yellow-400/20 mb-8">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                <span className="text-yellow-300 font-medium text-sm tracking-wide">EXCLUSIVE ACCESS • INSTITUTIONAL GRADE</span>
              </div>
              
              <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
                <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                  PRIVATE
                </span>
                <br />
                <span className="bg-gradient-to-r from-white via-gray-100 to-gray-200 bg-clip-text text-transparent">
                  EQUITY
                </span>
                <br />
                <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                  INVESTING
                </span>
                <br />
                <span className="bg-gradient-to-r from-white via-gray-100 to-gray-200 bg-clip-text text-transparent">
                  CLUB
                </span>
                <br />
              </h1>
              
              <p className="text-2xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
                Exclusive Series B+ opportunities now accessible
              </p>
              
              <div className="flex flex-col sm:inline-flex sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 bg-slate-900/90 backdrop-blur-sm rounded-2xl px-6 py-6 sm:px-10 border border-yellow-400/20 shadow-2xl mb-12">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center">
                    <span className="text-slate-950 font-bold text-lg">1</span>
                  </div>
                  <span className="text-yellow-400 font-semibold text-lg">Learn</span>
                </div>
                <ArrowRight className="text-yellow-400 w-6 h-6 hidden sm:block" />
                <div className="w-6 h-6 text-yellow-400 sm:hidden">⬇</div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center">
                    <span className="text-slate-950 font-bold text-lg">2</span>
                  </div>
                  <span className="text-yellow-400 font-semibold text-lg">Invest</span>
                </div>
                <ArrowRight className="text-yellow-400 w-6 h-6 hidden sm:block" />
                <div className="w-6 h-6 text-yellow-400 sm:hidden">⬇</div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center">
                    <span className="text-slate-950 font-bold text-lg">3</span>
                  </div>
                  <span className="text-yellow-400 font-semibold text-lg">Grow</span>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
                <div className="text-center">
                  <div className="text-4xl font-black text-yellow-400 mb-2">
                    50+
                  </div>
                  <div className="text-gray-400 font-medium">Trusted Members</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-yellow-400 mb-2">
                    €2,000,000
                  </div>
                  <div className="text-gray-400 font-medium">Capital Deployed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-yellow-400 mb-2">
                    10+
                  </div>
                  <div className="text-gray-400 font-medium">Years Experience</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-xl text-yellow-400 font-bold">
                  Join the Investment Club
                </p>

              </div>
            </div>
            
            <div className="flex justify-center">
              <button 
                onClick={() => scrollToSection('mission')}
                className="group animate-bounce bg-gradient-to-r from-yellow-400/20 to-yellow-300/20 p-4 rounded-full border border-yellow-400/50 hover:from-yellow-400/30 hover:to-yellow-300/30 transition-all duration-300 backdrop-blur-sm"
              >
                <ChevronDown className="w-8 h-8 text-yellow-400 group-hover:text-yellow-300 transition-colors duration-300" />
              </button>
            </div>
          </div>
        </section>
      </Parallax>

      {/* Mission Section - Medium Dark Blue */}
      <Parallax 
        strength={200}
        bgImage="data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23facc15' fill-opacity='0.03'%3E%3Cpolygon points='20 0 40 20 20 40 0 20'/%3E%3C/g%3E%3C/svg%3E"
        bgImageStyle={{opacity: 0.2}}
      >
        <section id="mission" className="py-24 px-6 bg-slate-900">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400/10 to-yellow-300/10 backdrop-blur-sm rounded-full px-6 py-2 border border-yellow-400/20 mb-8">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-300 font-medium text-sm tracking-wide">OUR MISSION</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-black mb-8 bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
                Democratising private equity investments
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-yellow-400 to-yellow-200 mx-auto mb-12 rounded-full"></div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="bg-slate-950/95 backdrop-blur-sm rounded-3xl p-8 border border-yellow-400/20 shadow-2xl">
                  <h3 className="text-2xl font-bold text-yellow-400 mb-6">The Vision</h3>
                  <p className="text-lg leading-relaxed text-gray-300">
                  <span className="text-yellow-400 font-semibold">With over a decade of experience in private equity, </span>  
                    I provide access to exclusive opportunities traditionally available only to institutional investors. 
                    
                                      </p>
                </div>
                
                <div className="bg-slate-950/95 backdrop-blur-sm rounded-3xl p-8 border border-yellow-400/20 shadow-2xl">
                  <h3 className="text-2xl font-bold text-yellow-400 mb-6">The Opportunity</h3>
                  <p className="text-lg leading-relaxed text-gray-300">
                    Our syndicate specialises in sourcing exclusive Series B+ private equity deals. We provide secure, 
                    trusted access to co-invest alongside <span className="text-yellow-400 font-semibold">world-class institutional players </span> 
                    in high-growth, disruptive companies.
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 to-yellow-200/15 rounded-3xl blur-xl"></div>
                <div className="relative bg-slate-950/98 backdrop-blur-sm rounded-3xl p-10 border border-yellow-400/30 shadow-2xl">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <TrendingUp className="w-10 h-10 text-slate-950" />
                    </div>
                    <h3 className="text-3xl font-bold text-yellow-400 mb-4">High ROI Focus</h3>
                  </div>
                  <p className="text-lg text-gray-300 text-center leading-relaxed">
                    We back ambitious founders and disruptive companies in high-growth sectors, 
                    always with a laser focus on <span className="text-yellow-400 font-semibold">exceptional return potential</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Parallax>

      {/* Success Story - Very Dark Blue */}
      <Parallax 
        strength={250}
        bgImage="data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23facc15' fill-opacity='0.04'%3E%3Cpath d='M40 0L80 40L40 80L0 40Z'/%3E%3C/g%3E%3C/svg%3E"
        bgImageStyle={{opacity: 0.25}}
      >
        <section id="success-story" className="py-24 px-6 bg-slate-950">
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/8 via-transparent to-transparent rounded-3xl blur-xl"></div>
              <div className="relative bg-slate-900/98 backdrop-blur-sm rounded-3xl p-12 border border-yellow-400/30 shadow-2xl">
                <div className="flex items-center justify-center mb-8">
                  <div className="flex items-center space-x-4 bg-gradient-to-r from-yellow-400/20 to-yellow-300/20 rounded-full px-8 py-4 border border-yellow-400/30">
                    <Star className="w-8 h-8 text-yellow-400" />
                    <h3 className="text-3xl font-black text-yellow-400">RECENT HIGHLIGHT</h3>
                    <Star className="w-8 h-8 text-yellow-400" />
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="grid md:grid-cols-3 gap-8 mb-8">
                    <div className="text-center">
                      <div className="text-5xl font-black text-yellow-400 mb-2">€2,000,000</div>
                      <div className="text-gray-300 font-medium">Capital Raised</div>
                    </div>
                    <div className="text-center">
                      <div className="text-5xl font-black text-yellow-400 mb-2">50+</div>
                      <div className="text-gray-300 font-medium">Investors</div>
                    </div>
                    <div className="text-center">
                      <div className="flex justify-center items-center h-16 mb-2">
                        <img 
                          src="/groq-icon-seeklogo.png" 
                          alt="Groq Logo" 
                          className="h-12 w-auto object-contain"
                        />
                      </div>
                      <div className="text-gray-300 font-medium">Groq</div>
                    </div>
                  </div>
                  
                  <p className="text-2xl text-gray-200 leading-relaxed max-w-4xl mx-auto">
                    We raised nearly <span className="text-yellow-400 font-bold">€2 million from 50+ investors</span> to 
                    invest in Groq, a pioneering AI hardware company also backed by <span className="text-yellow-400 font-bold">BlackRock </span> 
                    and leading the charge in accelerating inference performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Parallax>

      {/* Community Stats - Medium Dark Blue */}
      <section id="community" className="py-24 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-black mb-8 bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
              Backed by Industry Leaders
            </h3>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Join a diverse community of CEOs, CFOs, senior partners, professional investors, 
              and successful entrepreneurs from across Europe.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 to-yellow-200/15 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-slate-950/98 backdrop-blur-sm p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <Users className="w-8 h-8 text-slate-950" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-center">50+ Members</h4>
                <p className="text-gray-400 text-center">Community of high-net-worth individuals</p>
              </div>
            </div>
            
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 to-yellow-200/15 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-slate-950/98 backdrop-blur-sm p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-slate-950" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-center">€2M+ Deployed</h4>
                <p className="text-gray-400 text-center">Successfully invested across multiple deals</p>
              </div>
            </div>
            
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 to-yellow-200/15 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative bg-slate-950/98 backdrop-blur-sm p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-slate-950" />
                </div>
                <h4 className="text-2xl font-bold mb-3 text-center">Series B+ Exclusive</h4>
                <p className="text-gray-400 text-center">Access to institutional-grade opportunities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Very Dark Blue */}
      <Parallax 
        strength={350}
        bgImage="data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23facc15' fill-opacity='0.06'%3E%3Ccircle cx='50' cy='50' r='4'/%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3Ccircle cx='80' cy='20' r='2'/%3E%3Ccircle cx='20' cy='80' r='2'/%3E%3Ccircle cx='80' cy='80' r='2'/%3E%3C/g%3E%3C/svg%3E"
        bgImageStyle={{opacity: 0.3}}
      >
        <section id="how-it-works" className="py-24 px-6 bg-slate-950">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400/10 to-yellow-300/10 backdrop-blur-sm rounded-full px-6 py-2 border border-yellow-400/20 mb-8">
                <CheckCircle className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-300 font-medium text-sm tracking-wide">HOW IT WORKS</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-black mb-8 bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
                Simple. Transparent.
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-yellow-400 to-yellow-200 mx-auto rounded-full"></div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/25 to-yellow-200/25 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative w-24 h-24 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center text-3xl font-black text-slate-950 mx-auto shadow-2xl">
                    01
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-6 text-yellow-400">Join the Club</h3>
                <p className="text-gray-300 leading-relaxed text-lg">
                  Become part of our exclusive community of sophisticated investors. 
                  <span className="text-yellow-400 font-semibold"> Membership is completely free</span> with no hidden fees or commitments.
                </p>
              </div>
              
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/25 to-yellow-200/25 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative w-24 h-24 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center text-3xl font-black text-slate-950 mx-auto shadow-2xl">
                    02
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-6 text-yellow-400">Access Curated Deals</h3>
                <p className="text-gray-300 leading-relaxed text-lg">
                  Get exclusive access to <span className="text-yellow-400 font-semibold">hand-picked Series B+ opportunities </span> 
                  throughout the year. Each deal is thoroughly vetted by our expert team.
                </p>
              </div>
              
              <div className="text-center group">
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/25 to-yellow-200/25 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative w-24 h-24 bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full flex items-center justify-center text-3xl font-black text-slate-950 mx-auto shadow-2xl">
                    03
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-6 text-yellow-400">Invest at Will</h3>
                <p className="text-gray-300 leading-relaxed text-lg">
                  <span className="text-yellow-400 font-semibold">You're in complete control.</span> 
                  Like a deal? Invest. Don't like it? Skip it. Zero pressure, maximum flexibility.
                </p>
              </div>
            </div>
            
            <div className="text-center mt-16">
              <p className="text-3xl text-yellow-400 font-black">
                It's that simple.
              </p>
            </div>
          </div>
        </section>
      </Parallax>

      {/* CTA Section - Very Dark Blue */}
      <Parallax 
        strength={400}
        bgImage="data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23facc15' fill-opacity='0.08'%3E%3Cstar points='60,10 72,42 108,42 80,65 90,98 60,80 30,98 40,65 12,42 48,42' /%3E%3C/g%3E%3C/svg%3E"
        bgImageStyle={{opacity: 0.4}}
      >
        <section className="py-24 px-6 bg-slate-950">
          <div className="max-w-5xl mx-auto text-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/15 to-yellow-200/15 rounded-3xl blur-xl"></div>
              <div className="relative bg-slate-900/98 backdrop-blur-sm rounded-3xl p-12 border border-yellow-400/30 shadow-2xl">
                <h2 className="text-5xl md:text-6xl font-black mb-8 bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">
                  Ready to Join the Club?
                </h2>
                <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                  If you're curious about private equity or want to explore exclusive investment opportunities, 
                  let's connect. I'd love to welcome you to our community! 
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <a 
                    href="https:/calendly.com/loic-cluboneforall/30min"
                    className="group relative bg-gradient-to-r from-yellow-400 to-yellow-300 text-slate-950 px-10 py-5 rounded-full font-black text-xl hover:from-yellow-300 hover:to-yellow-200 transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-yellow-400/25 inline-block"
                  >
                    <span className="relative z-10 flex items-center space-x-2">
                      <span>Contact me</span>
                      <ArrowUpRight className="w-5 h-5" />
                    </span>
                    <div className="absolute inset-0 bg-white/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Parallax>

      {/* Footer - Medium Dark Blue */}
      <footer className="py-16 px-6 bg-slate-900 border-t border-yellow-400/20">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <div className="relative inline-block">
              <img 
                src="/OFA.png" 
                alt="OneForAll Logo" 
                className="relative h-16 w-auto object-contain"
              />
            </div>
          </div>
          
          <p className="text-gray-400 mb-6 text-lg">Democratising access to top-tier private equity investments</p>
          <a 
            href="https://www.linkedin.com/in/loicgerard/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-yellow-400 font-bold text-2xl mb-8 hover:text-yellow-300 transition-colors duration-300 inline-block"
          >
            Loïc Gerard
          </a>
          
          <div className="border-t border-yellow-400/20 pt-8">
            <p className="text-gray-500 text-sm">© 2025 OneForAll Investment Syndicate. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
